// cart-elements.js — depends on shared.js (api, dom, debounce, CustomHTMLElement, CART_ROUTES, isDev, updateGlobalCartCount, refreshCartSection, showCartError)
// Line items declare which section re-renders them via data-refresh-section / data-refresh-target.

const refreshLineItem = (lineItem) =>
  refreshCartSection(
    lineItem.dataset.refreshSection,
    lineItem.dataset.refreshTarget,
  );

const removeCartItem = async (lineItem, variantKey) => {
  try {
    const response = await api.change(CART_ROUTES.change, {
      id: variantKey,
      quantity: 0,
    });

    updateGlobalCartCount(response.data.item_count);
    await refreshLineItem(lineItem);
  } catch (error) {
    showCartError(error.response?.data?.description);
    if (isDev) console.error('Could not remove item from cart:', error);
  }
};

class AdjustQuantityButton extends CustomHTMLElement {
  constructor() {
    super();
    this.debouncedAdjust = debounce(this.adjustQuantity.bind(this), 300);
  }

  onClick(event) {
    const operation = this.classList.contains('minus') ? 'minus' : 'plus';
    this.debouncedAdjust(operation);
  }

  async adjustQuantity(operation) {
    const lineItem = this.closest('[data-key]');
    const variantKey = lineItem?.getAttribute('data-key');

    if (!variantKey) {
      showCartError();
      if (isDev) console.error('No variant key found');
      return;
    }

    const spinner = lineItem.querySelector('.loading-spinner');
    const quantityInput = lineItem.querySelector('.global-cart-item-quantity');

    if (!quantityInput) {
      showCartError();
      if (isDev) console.error('No quantity input found');
      return;
    }

    dom.toggleSpinner(spinner, true);

    try {
      const currentQuantity = parseInt(quantityInput.value) || 0;
      const newQuantity =
        operation === 'minus' ? currentQuantity - 1 : currentQuantity + 1;

      if (newQuantity <= 0) {
        await removeCartItem(lineItem, variantKey);
        return;
      }

      const response = await api.change(CART_ROUTES.change, {
        id: variantKey,
        quantity: newQuantity,
      });

      quantityInput.value = newQuantity;
      updateGlobalCartCount(response.data.item_count);
      await refreshLineItem(lineItem);
    } catch (error) {
      showCartError(error.response?.data?.description);
      if (isDev) console.error('Could not update item quantity:', error);
    } finally {
      dom.toggleSpinner(spinner, false);
    }
  }
}

class RemoveItemButton extends CustomHTMLElement {
  async onClick(event) {
    const lineItem = this.closest('[data-key]');
    const variantKey = lineItem?.getAttribute('data-key');

    if (!variantKey) {
      showCartError();
      if (isDev) console.error('No variant key found for removal');
      return;
    }

    await removeCartItem(lineItem, variantKey);
  }
}

if (!customElements.get('adjust-quantity-button'))
  customElements.define('adjust-quantity-button', AdjustQuantityButton);
if (!customElements.get('remove-item-button'))
  customElements.define('remove-item-button', RemoveItemButton);

// Delegated on document so popovers keep working after a section refresh replaces their markup.
const popoverFor = (button) => {
  const controlledPopover = document.getElementById(
    button.getAttribute('aria-controls') || '',
  );
  if (controlledPopover) return controlledPopover;

  const sibling = button.nextElementSibling;
  return sibling?.classList.contains('popover') ? sibling : null;
};

const closePopover = (button) => {
  button.setAttribute('aria-expanded', 'false');
  popoverFor(button)?.classList.add('hidden');
};

const openPopoverButtons = () =>
  document.querySelectorAll('.info-button[aria-expanded="true"]');

document.addEventListener('click', (event) => {
  const button = event.target.closest('.info-button');
  openPopoverButtons().forEach((open) => {
    if (open !== button) closePopover(open);
  });
  if (!button) return;
  event.preventDefault();
  const popover = popoverFor(button);
  if (!popover) return;
  const isOpen = button.getAttribute('aria-expanded') === 'true';
  button.setAttribute('aria-expanded', String(!isOpen));
  popover.classList.toggle('hidden', isOpen);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const openButtons = openPopoverButtons();
  if (openButtons.length === 0) return;
  // Keep the same press from also reaching the cart drawer's window-level Escape.
  event.stopPropagation();
  openButtons.forEach((open) => {
    closePopover(open);
    open.focus();
  });
});
